import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import PageCard from './components/PageCard';
import LoadingModal from './components/modals/LoadingModal';
import { formatCurrencyView } from './utils/currencyUtils';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MESES_EXT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MES_FIELD = ['janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const num = v => Number(v) || 0;
const sumArr = arr => arr.reduce((a, b) => a + b, 0);
const monthOf = dateStr => (dateStr ? new Date(dateStr + 'T00:00:00').getMonth() : -1);

const formatPct = value => {
  const pct = num(value);
  return `${pct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
};

function StatCard({ icon, label, value, sub, color, bg, iconColor }) {
  return (
    <div className="card shadow-sm border-0 h-100" style={{ borderLeft: `4px solid ${color}`, borderRadius: '0.75rem' }}>
      <div className="card-body py-3 d-flex justify-content-between align-items-center">
        <div className="min-width-0">
          <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: 11, letterSpacing: '.3px' }}>
            {label}
          </div>
          <div className="fw-bold fs-5" style={{ color: '#1f2937' }}>
            {value}
          </div>
          {sub && <div className="small text-muted">{sub}</div>}
        </div>
        <div
          className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 46, height: 46, background: bg }}
        >
          <i className={`bi ${icon} fs-5`} style={{ color: iconColor || color }} />
        </div>
      </div>
    </div>
  );
}

function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.max((Math.abs(value) / max) * 100, value !== 0 ? 3 : 1.5) : 1.5;
  return (
    <div
      title={formatCurrencyView(value)}
      className="rounded-top"
      style={{
        width: '100%',
        height: `${pct}%`,
        background: color,
        minHeight: 2,
        transition: 'height .4s ease',
      }}
    />
  );
}

function FluxoChart({ receitas, despesas }) {
  const max = Math.max(...[...receitas, ...despesas], 0);
  return (
    <div>
      <div className="d-flex align-items-end" style={{ height: 170 }}>
        {MESES.map((m, i) => (
          <div key={m} className="flex-grow-1 h-100 d-flex flex-column justify-content-end align-items-center" style={{ gap: 2 }}>
            <div className="d-flex align-items-end justify-content-center w-100 h-100" style={{ gap: 3 }}>
              <MiniBar value={receitas[i]} max={max} color="#198754" />
              <MiniBar value={despesas[i]} max={max} color="#dc3545" />
            </div>
            <span className="text-muted text-center" style={{ fontSize: 10 }}>
              {m}
            </span>
          </div>
        ))}
      </div>
      <div className="d-flex justify-content-center gap-4 mt-2 small">
        <span>
          <span className="badge" style={{ background: '#198754' }}>&nbsp;</span> Receitas
        </span>
        <span>
          <span className="badge" style={{ background: '#dc3545' }}>&nbsp;</span> Despesas pagas
        </span>
      </div>
    </div>
  );
}

function SaldoChart({ saldos }) {
  const max = Math.max(...saldos.map(Math.abs), 0);
  return (
    <div>
      <div className="d-flex align-items-end" style={{ height: 150 }}>
        {MESES.map((m, i) => (
          <div key={m} className="flex-grow-1 h-100 d-flex flex-column justify-content-end align-items-center" style={{ gap: 2 }}>
            <div className="d-flex align-items-end justify-content-center w-100 h-100">
              <MiniBar value={saldos[i]} max={max} color={saldos[i] < 0 ? '#dc3545' : '#0d6efd'} />
            </div>
            <span className="text-muted text-center" style={{ fontSize: 10 }}>
              {m}
            </span>
          </div>
        ))}
      </div>
      <div className="d-flex justify-content-center mt-2 small">
        <span>
          <span className="badge" style={{ background: '#0d6efd' }}>&nbsp;</span> Saldo do sistema por mês
        </span>
      </div>
    </div>
  );
}

function ProgressRow({ label, value, total, color = 'success' }) {
  const pct = total > 0 ? Math.min(100, Math.round((num(value) / num(total)) * 100)) : 0;
  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between align-items-center small mb-1">
        <span className="me-2" title={label} style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
          {label}
        </span>
        <span className="text-nowrap fw-semibold flex-shrink-0">{formatCurrencyView(value)}</span>
      </div>
      <div className="progress" style={{ height: 8, borderRadius: 10 }}>
        <div className={`progress-bar bg-${color}`} role="progressbar" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Home() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = parseInt(localStorage.getItem('anoBase')) || new Date().getFullYear();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const mesRef = anoBase === now.getFullYear() ? now.getMonth() + 1 : 12;

  useEffect(() => {
    if (!cnpj) {
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    const carregar = async () => {
      const minAnoRes = await axios
        .get(`${API_BASE_URL.ORCAMENTO}/ano-inicial`, { params: { cnpjAssociacao: cnpj }, headers })
        .catch(() => null);
      const anoIniExec = minAnoRes?.data || anoBase;
      Promise.allSettled([
        axios.get(`${API_BASE_URL.RECEITA}/all`, { params: { cnpjAssociacao: cnpj, ano: anoBase }, headers }),
        axios.get(`${API_BASE_URL.DESPESA}/all`, { params: { cnpjAssociacao: cnpj, ano: anoBase }, headers }),
        axios.get(`${API_BASE_URL.RENDIMENTO}/all`, { params: { cnpjAssociacao: cnpj, ano: anoBase }, headers }),
        axios.get(`${API_BASE_URL.SALDO}/all`, { params: { cnpjAssociacao: cnpj, ano: anoBase }, headers }),
        axios.get(`${API_BASE_URL.SALDO_INVESTIMENTO}/all`, { params: { cnpjAssociacao: cnpj, ano: anoBase }, headers }),
        axios.get(`${API_BASE_URL.CONTA}/all`, { params: { cnpjAssociacao: cnpj }, headers }),
        axios.get(`${API_BASE_URL.EXECUCAO_ORCAMENTARIA}/periodo`, {
          params: { cnpjAssociacao: cnpj, anoInicial: anoIniExec, mesInicial: 1, anoFinal: anoBase, mesFinal: mesRef },
          headers,
        }),
      ]).then(results => {
        const list = res => (res.status === 'fulfilled' && Array.isArray(res.value?.data) ? res.value.data : []);
        setData({
          receitas: list(results[0]),
          despesas: list(results[1]),
          rendimentos: list(results[2]),
          saldos: list(results[3]),
          saldosInv: list(results[4]),
          contas: list(results[5]),
          execucao: results[6].status === 'fulfilled' ? results[6].value?.data : null,
          mesRef,
          anoIniExec,
        });
        setLoading(false);
      });
    };
    carregar();
  }, [cnpj, token, anoBase, mesRef]);

  const m = useMemo(() => {
    if (!data) return null;
    const { receitas, despesas, rendimentos, saldos, saldosInv, contas, execucao, mesRef: ref } = data;

    const pagas = despesas.filter(d => d.dataPagamento);
    const abertas = despesas.filter(d => !d.dataPagamento && !d.dataAnulacao);

    // Receitas
    const receitasAno = sumArr(receitas.map(r => num(r.valor)));
    const receitasMes = sumArr(receitas.filter(r => monthOf(r.data) === ref - 1).map(r => num(r.valor)));

    // Despesas
    const despesasAno = sumArr(pagas.map(d => num(d.valor)));
    const despesasMes = sumArr(pagas.filter(d => monthOf(d.dataPagamento) === ref - 1).map(d => num(d.valor)));
    const abertasAno = sumArr(abertas.map(d => num(d.valor)));

    // Rendimentos
    const rendAno = sumArr(rendimentos.map(r => sumArr(MES_FIELD.map(f => num(r[f])))));
    const rendMes = sumArr(rendimentos.map(r => num(r[MES_FIELD[ref - 1]])));

    // Saldo do sistema por mês (agregado por conta/programa)
    const saldoMensal = MES_FIELD.map(f =>
      sumArr(saldos.map(s => num(s[f]))) + sumArr(saldosInv.map(s => num(s[f])))
    );

    // Fluxo mensal
    const receitasPorMes = MES_FIELD.map(() => 0);
    receitas.forEach(r => {
      const idx = monthOf(r.data);
      if (idx >= 0) receitasPorMes[idx] += num(r.valor);
    });
    const despesasPorMes = MES_FIELD.map(() => 0);
    pagas.forEach(d => {
      const idx = monthOf(d.dataPagamento);
      if (idx >= 0) despesasPorMes[idx] += num(d.valor);
    });

    // Saldo por conta (último mês com lançamento)
    const totalPorMesConta = (records, numConta) =>
      MES_FIELD.map((f, i) =>
        sumArr(records.filter(r => r.numConta === numConta).map(r => num(r[f])))
      );
    const lastMonthWithRecord = (numConta, a, b) => {
      for (let i = ref - 1; i >= 0; i--) {
        const hasA = a.some(r => r.numConta === numConta && r[MES_FIELD[i]] != null);
        const hasB = b.some(r => r.numConta === numConta && r[MES_FIELD[i]] != null);
        if (hasA || hasB) return i;
      }
      return -1;
    };
    const saldoContas = contas
      .map(c => {
        const a = totalPorMesConta(saldos, c.numConta);
        const b = totalPorMesConta(saldosInv, c.numConta);
        const idx = lastMonthWithRecord(c.numConta, saldos, saldosInv);
        if (idx < 0) return null;
        return {
          numConta: c.numConta,
          banco: c.nomeBanco || '-',
          mes: MESES_EXT[idx],
          total: a[idx] + b[idx],
          invest: b[idx],
        };
      })
      .filter(Boolean);
    const saldoContasTotal = sumArr(saldoContas.map(s => s.total));
    const investTotal = sumArr(saldoContas.map(s => s.invest));

    // Execução orçamentária
    const orcado = num(execucao?.totalOrcado);
    const pago = num(execucao?.totalPago);
    const saldoDisp = num(execucao?.totalSaldoDisponivel);
    const pctGeral = num(execucao?.percentualExecutadoGeral);
    const porProgramaExec = (execucao?.porPrograma || [])
      .slice()
      .sort((x, y) => num(y.orcado) - num(x.orcado))
      .slice(0, 6);

    // Despesas por programa (pagas)
    const progMap = {};
    pagas.forEach(d => {
      const k = d.descricaoPrograma || 'Sem programa';
      progMap[k] = (progMap[k] || 0) + num(d.valor);
    });
    const despesasPorPrograma = Object.entries(progMap)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);

    // Top favorecidos (pagas)
    const favMap = {};
    pagas.forEach(d => {
      const k = d.nomeFavorecido || d.idFavorecido || 'Não informado';
      favMap[k] = (favMap[k] || 0) + num(d.valor);
    });
    const topFavorecidos = Object.entries(favMap)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);

    return {
      mesRef: ref,
      anoIniExec: data.anoIniExec,
      receitasAno,
      receitasMes,
      despesasAno,
      despesasMes,
      abertasAno,
      rendAno,
      rendMes,
      saldoMensal,
      receitasPorMes,
      despesasPorMes,
      saldoContas,
      saldoContasTotal,
      investTotal,
      orcado,
      pago,
      saldoDisp,
      pctGeral,
      porProgramaExec,
      despesasPorPrograma,
      topFavorecidos,
    };
  }, [data]);

  if (loading) return <LoadingModal loading={true} />;

  return (
    <PageCard title="Home" icon="bi-house" showHeader={false} scrollable={false}>
      <div className="container-fluid px-0 d-flex flex-column" style={{ height: '100%', minHeight: 0 }}>
        {!m ? (
          <div className="alert alert-info">
            <i className="bi bi-info-circle me-2"></i>
            Nenhum dado financeiro encontrado para o ano base {anoBase}. Preencha os módulos de{' '}
            <strong>Receitas</strong>, <strong>Despesas</strong> e <strong>Saldos</strong> para visualizar o painel.
          </div>
        ) : (
          <>
            {/* KPIs fixos */}
            <div className="flex-shrink-0">
              <div className="row g-3">
              <div className="col-6 col-lg-3">
                <StatCard
                  icon="bi-wallet2"
                  label="Saldo em Contas"
                  value={formatCurrencyView(m.saldoContasTotal)}
                  sub={m.investTotal !== 0 ? `${formatCurrencyView(m.investTotal)} em investimento` : `${m.saldoContas.length} contas com saldo`}
                  color="#0d6efd"
                  bg="#e7f1ff"
                />
              </div>
              <div className="col-6 col-lg-3">
                <StatCard
                  icon="bi-graph-up-arrow"
                  label="Receitas do Ano"
                  value={formatCurrencyView(m.receitasAno)}
                  sub={`${formatCurrencyView(m.receitasMes)} em ${MESES_EXT[m.mesRef - 1]}`}
                  color="#198754"
                  bg="#e7f8ef"
                />
              </div>
              <div className="col-6 col-lg-3">
                <StatCard
                  icon="bi-graph-down-arrow"
                  label="Despesas Pagas"
                  value={formatCurrencyView(m.despesasAno)}
                  sub={`${formatCurrencyView(m.despesasMes)} no mês · ${formatCurrencyView(m.abertasAno)} em aberto`}
                  color="#dc3545"
                  bg="#fdecec"
                />
              </div>
              <div className="col-6 col-lg-3">
                <StatCard
                  icon="bi-graph-up"
                  label="Rendimentos do Ano"
                  value={formatCurrencyView(m.rendAno)}
                  sub={`${formatCurrencyView(m.rendMes)} em ${MESES_EXT[m.mesRef - 1]}`}
                  color="#6f42c1"
                  bg="#f1e9fb"
                />
              </div>
            </div>
            </div>

            {/* Cards abaixo em card scrollável */}
            <div className="card border-0 flex-grow-1 shadow-sm mt-3" style={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div className="flex-grow-1" style={{ overflowY: 'auto', minHeight: 0, padding: '12px' }}>
                <div className="row g-3 mb-3">
              <div className="col-lg-7">
                <div className="card shadow-sm h-100">
                  <div className="card-header bg-white fw-bold">
                    <i className="bi bi-bar-chart-line me-2 text-success"></i>
                    Fluxo Mensal
                  </div>
                  <div className="card-body">
                    <FluxoChart receitas={m.receitasPorMes} despesas={m.despesasPorMes} />
                  </div>
                </div>
              </div>
              <div className="col-lg-5">
                <div className="card shadow-sm h-100">
                  <div className="card-header bg-white fw-bold">
                    <i className="bi bi-pie-chart me-2 text-primary"></i>
                    Execução Orçamentária
                  </div>
                  <div className="card-body">
                    <div className="text-center mb-2">
                      <div className="display-6 fw-bold" style={{ color: '#1f2937' }}>
                        {formatPct(m.pctGeral)}
                      </div>
                      <div className="text-muted small">
                        do orçamento executado
                        {m.anoIniExec !== anoBase && (
                          <span>
                            {' '}· jan/{m.anoIniExec} a {MESES[m.mesRef - 1]}/{anoBase}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="progress mb-3" style={{ height: 12, borderRadius: 10 }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        style={{ width: `${Math.min(100, m.pctGeral)}%`, background: 'linear-gradient(90deg,#076307,#0a7a45)' }}
                      />
                    </div>
                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-muted">Orçado</span>
                      <span className="fw-semibold">{formatCurrencyView(m.orcado)}</span>
                    </div>
                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-muted">Pago</span>
                      <span className="fw-semibold text-danger">{formatCurrencyView(m.pago)}</span>
                    </div>
                    <div className="d-flex justify-content-between small mb-3">
                      <span className="text-muted">Saldo disponível</span>
                      <span className="fw-semibold text-success">{formatCurrencyView(m.saldoDisp)}</span>
                    </div>
                    {m.porProgramaExec.length > 0 && (
                      <>
                        <hr className="my-2" />
                        <div className="fw-semibold small mb-2 text-muted">Por programa · cada um no seu período</div>
                        {m.porProgramaExec.map(p => {
                          const pct = num(p.percentualExecutado);
                          return (
                            <div key={p.idPrograma || p.descricaoPrograma} className="mb-3">
                              <div className="d-flex justify-content-between align-items-center small mb-1">
                                <span className="text-truncate me-2" title={p.descricaoPrograma || `Programa ${p.idPrograma}`}>
                                  {p.descricaoPrograma || `Programa ${p.idPrograma}`}
                                </span>
                                <span
                                  className={`text-nowrap fw-bold ${pct > 100 ? 'text-danger' : pct >= 80 ? 'text-success' : 'text-primary'}`}
                                >
                                  {formatPct(pct)}
                                </span>
                              </div>
                              <div className="progress mb-1" style={{ height: 8, borderRadius: 10 }}>
                                <div
                                  className="progress-bar progress-bar-striped"
                                  style={{ width: `${Math.min(100, pct)}%`, background: pct > 100 ? '#dc3545' : '#0d6efd' }}
                                />
                              </div>
                              <div className="d-flex justify-content-between small text-muted">
                                <span>Orçado {formatCurrencyView(num(p.orcado) + num(p.redistribuido))}</span>
                                <span>Pago {formatCurrencyView(num(p.pago))}</span>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Saldo por conta + Saldo mensal */}
            <div className="row g-3 mb-3">
              <div className="col-lg-7">
                <div className="card shadow-sm h-100">
                  <div className="card-header bg-white fw-bold">
                    <i className="bi bi-bank me-2 text-info"></i>
                    Saldo por Conta
                    <span className="badge bg-light text-dark ms-2 fw-normal">último mês com lançamento</span>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover table-sm mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="px-3">Conta</th>
                            <th>Banco</th>
                            <th className="text-center">Mês</th>
                            <th className="text-center">Investimento</th>
                            <th className="text-end pe-3">Saldo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {m.saldoContas.map(c => (
                            <tr key={c.numConta}>
                              <td className="px-3 fw-semibold">{c.numConta}</td>
                              <td>{c.banco}</td>
                              <td className="text-center text-muted">{c.mes}</td>
                              <td className="text-center">{c.invest !== 0 ? formatCurrencyView(c.invest) : '-'}</td>
                              <td className="text-end pe-3 fw-semibold">{formatCurrencyView(c.total)}</td>
                            </tr>
                          ))}
                          {m.saldoContas.length === 0 && (
                            <tr>
                              <td colSpan="5" className="text-center text-muted py-3">
                                Nenhum saldo informado no ano base.
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {m.saldoContas.length > 0 && (
                          <tfoot className="table-light fw-bold">
                            <tr>
                              <td colSpan="4" className="px-3">
                                Total
                              </td>
                              <td className="text-end pe-3">{formatCurrencyView(m.saldoContasTotal)}</td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-5">
                <div className="card shadow-sm h-100">
                  <div className="card-header bg-white fw-bold">
                    <i className="bi bi-cash-stack me-2 text-primary"></i>
                    Saldo do Sistema por Mês
                  </div>
                  <div className="card-body">
                    <SaldoChart saldos={m.saldoMensal} />
                  </div>
                </div>
              </div>
            </div>

            {/* Despesas por programa + Top favorecidos */}
            <div className="row g-3">
              <div className="col-lg-6">
                <div className="card shadow-sm h-100">
                  <div className="card-header bg-white fw-bold">
                    <i className="bi bi-tags me-2 text-danger"></i>
                    Despesas Pagas por Programa
                  </div>
                  <div className="card-body">
                    {m.despesasPorPrograma.length > 0 ? (
                      m.despesasPorPrograma.map(d => (
                        <ProgressRow key={d.label} label={d.label} value={d.value} total={m.despesasAno} color="danger" />
                      ))
                    ) : (
                      <div className="text-center text-muted py-3">Sem despesas pagas no ano base.</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="card shadow-sm h-100">
                  <div className="card-header bg-white fw-bold">
                    <i className="bi bi-people me-2 text-warning"></i>
                    Top Favorecidos (Despesas Pagas)
                  </div>
                  <div className="card-body">
                    {m.topFavorecidos.length > 0 ? (
                      m.topFavorecidos.map(d => (
                        <ProgressRow key={d.label} label={d.label} value={d.value} total={m.despesasAno} color="warning" />
                      ))
                    ) : (
                      <div className="text-center text-muted py-3">Sem despesas pagas no ano base.</div>
                    )}
                  </div>
                </div>
              </div>
              </div>
              </div>
              </div>
          </>
        )}
      </div>
    </PageCard>
  );
}

export default Home;
